import { useMemo } from 'react';
import { useQuery } from 'react-query';

import useBulkRequestItem from '@app/features/bulkRequest/hooks/useBulkRequestItem';
import { useDocumentPermission } from '@app/features/documents/hooks/useDocumentPermission';
import useAuthority from '@app/hooks/useAuthority';
import useMemberPermissions from '@app/hooks/useMemberPermission';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import { useCurrentMemberGroupList, useCurrentUserMemberId } from '@app/hooks/workspace';
import getAllList from '@app/libs/getAllList';
import { getNetPermission } from '@app/libs/permission/getNetPermission';
import { getBulkRequestRoles } from '@app/store/transport-layer/bulk-request/bulk-requests/roles';

type BulkRequestPermission = {
  isLoading: boolean;
  hasBulkRequestAuthority: boolean;
  readAllBulkRequestPermission: { isPermitted: boolean; message: string };
  manageBulkRequestPermission: { isPermitted: boolean; message: string };
};
type BulkRequestDetailPermission = BulkRequestPermission & {
  readablePermission: { isPermitted: boolean; message: string };
  editablePermission: { isPermitted: boolean; message: string };
  shareablePermission: { isPermitted: boolean; message: string };
  schedulePermission: { isPermitted: boolean; message: string };
};

export function useBulkRequestPermission(): BulkRequestPermission;
export function useBulkRequestPermission({
  bulkRequestId,
}: {
  bulkRequestId: string;
}): BulkRequestDetailPermission;
export function useBulkRequestPermission({
  bulkRequestId,
}: {
  bulkRequestId?: string;
} = {}) {
  // 요금제 권한 판단
  const authority = useAuthority();
  const hasBulkRequestAuthority = authority.data?.permissions.BULK_REQUEST === true;

  // 워크스페이스 역할 판단
  const { permissionMap, isLoading } = useMemberPermissions();
  const readAllBulkRequestPermission = getNetPermission({
    hasAuthorityPermission: hasBulkRequestAuthority,
    hasRolePermission: permissionMap.READ_ALL_BULK_REQUEST,
  });
  // TODO(@YanZisuka): 대량전송 폴링 개선
  const bulkRequest = useBulkRequestItem(bulkRequestId ?? '');
  const currentUserMemberId = useCurrentUserMemberId();
  const manageBulkRequestPermission = (() => {
    if (bulkRequestId && bulkRequest.data?.memberId !== currentUserMemberId) {
      return {
        isPermitted: false,
        message: '다른 멤버의 대량전송 설정은\n변경할 수 없습니다.',
      };
    }
    return getNetPermission({
      hasAuthorityPermission: hasBulkRequestAuthority,
      hasRolePermission: permissionMap.MANAGE_BULK_REQUEST,
    });
  })();

  // 리소스 역할 판단
  const workspaceId = useWorkspaceId();
  const currentMemberGroups = useCurrentMemberGroupList();
  const bulkRequestRoles = useQuery({
    queryKey: [
      {
        path: ['workspaces', workspaceId, 'bulk-requests', bulkRequestId, 'roles'],
        type: 'query',
        service: 'bulkRequest',
        input: {
          workspaceId,
          bulkRequestId: bulkRequestId ?? '',
          params: {
            subjects: [
              `MEMBER/${currentUserMemberId}` as const,
              ...currentMemberGroups.map<`GROUP/${string}`>((g) => `GROUP/${g.id}` as const),
            ],
          },
        },
      },
    ],
    queryFn: ({ queryKey: [{ input }] }) =>
      getAllList({
        queryFn: async (offset, limit) => {
          const { count, roles } = await getBulkRequestRoles(
            input.workspaceId,
            input.bulkRequestId,
            {
              offset,
              limit,
              subjects: input.params.subjects,
            }
          );
          return { count, list: roles };
        },
      }),
    enabled: !!workspaceId && !!bulkRequestId && !!currentUserMemberId,
    staleTime: 5 * 1000,
  });
  const hasViewerRole = useMemo(
    () => bulkRequestRoles.data?.some((role) => role.name === 'VIEWER') ?? false,
    [bulkRequestRoles.data]
  );
  const hasEditorRole = useMemo(
    () => bulkRequestRoles.data?.some((role) => role.name === 'EDITOR') ?? false,
    [bulkRequestRoles.data]
  );
  const readablePermission = getNetPermission({
    hasAuthorityPermission: hasBulkRequestAuthority,
    hasRolePermission: readAllBulkRequestPermission.isPermitted || hasViewerRole,
  });
  const editablePermission = {
    ...getNetPermission({
      hasAuthorityPermission: hasBulkRequestAuthority,
      hasRolePermission: manageBulkRequestPermission.isPermitted || hasEditorRole,
    }),
    message:
      manageBulkRequestPermission.message ||
      (hasEditorRole ? '' : '다른 멤버의 대량전송 설정은\n변경할 수 없습니다.'),
  };

  // 추가공유 권한 판단
  const { assignDocumentPermission } = useDocumentPermission();

  const shareablePermission = {
    isPermitted: assignDocumentPermission.isPermitted && editablePermission.isPermitted,
    message: assignDocumentPermission.message || editablePermission.message,
  };

  // 예약 전송 권한 판단

  const hasScheduledRequestAuthority = authority.data?.permissions.SCHEDULED_REQUEST === true;

  const schedulePermission = {
    isPermitted: hasScheduledRequestAuthority && editablePermission.isPermitted,
    message: hasScheduledRequestAuthority ? editablePermission.message : '요금제 상담 필요',
  };

  if (bulkRequestId) {
    return {
      isLoading:
        isLoading || authority.isLoading || bulkRequest.isLoading || bulkRequestRoles.isLoading,
      hasBulkRequestAuthority,
      readAllBulkRequestPermission,
      manageBulkRequestPermission,
      readablePermission,
      editablePermission,
      shareablePermission,
      schedulePermission,
    } as BulkRequestDetailPermission;
  }

  return {
    isLoading: isLoading || authority.isLoading || bulkRequest.isLoading,
    hasBulkRequestAuthority,
    readAllBulkRequestPermission,
    manageBulkRequestPermission,
  };
}
